// Exports a table's rows as .xlsx (or .csv when asked for, or if the spreadsheet writer can't run).
// `columns` is [{ label, value: (row) => string | number | null }], so callers pass what the table shows.
import { sortByAccessor } from "./sortRows";

const cell = (v) => (v == null || v === "" ? null : typeof v === "number" ? v : String(v));

export const toSheetRows = (columns, rows) => [
  columns.map((c) => c.label),
  ...rows.map((row) => columns.map((c) => cell(c.value(row)))),
];

// A leading = + - @ makes Excel read a text cell as a formula; the apostrophe keeps it text.
const csvField = (v) => {
  const s = v == null ? "" : String(v);
  const safe = /^[=+\-@]/.test(s) && Number.isNaN(Number(s)) ? `'${s}` : s;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const toCsv = (sheetRows) => sheetRows.map((r) => r.map(csvField).join(",")).join("\r\n");

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// What an export dialog asked for, applied to the rows: optionally ordered by one column, then gathered by group
// (in the order groups first appear) with the group as a leading column.
export const shapeExport = ({ rows, columns, sort = null, group = null }) => {
  let shaped = sort ? sortByAccessor(rows, sort, columns.find((c) => c.key === sort.key)?.value) : rows;
  if (!group) return { rows: shaped, columns };
  const keyOf = (row) => group.get(row) || "None";
  const order = [...new Set(shaped.map(keyOf))];
  shaped = order.flatMap((key) => shaped.filter((row) => keyOf(row) === key));
  return { rows: shaped, columns: [{ label: group.label, value: keyOf }, ...columns] };
};

const saveCsv = (sheetRows, filename) => {
  download(new Blob([`\uFEFF${toCsv(sheetRows)}`], { type: "text/csv;charset=utf-8" }), `${filename}.csv`);
  return "csv";
};

// One workbook of several sheets, each { name, columns, rows }; as csv only the first sheet is saved.
export async function exportWorkbook({ sheets, filename, format = "xlsx" }) {
  const sheetData = sheets.map((s) => toSheetRows(s.columns, s.rows));
  if (format === "csv") return saveCsv(sheetData[0], filename);
  try {
    const { default: writeXlsxFile } = await import("write-excel-file/browser");
    await writeXlsxFile(
      sheetData.map((rows, i) => ({
        data: [rows[0].map((value) => ({ value, fontWeight: "bold" })), ...rows.slice(1)],
        sheet: sheets[i].name.slice(0, 31),
      }))
    ).toFile(`${filename}.xlsx`);
    return "xlsx";
  } catch {
    return saveCsv(sheetData[0], filename);
  }
}

// Rows that arrive as plain arrays under a list of header labels, as the server's project and lead exports do.
export const exportPlainRows = ({ columns, rows, filename, format }) =>
  exportTable({ columns: columns.map((label, i) => ({ label, value: (r) => r[i] })), rows, filename, format });

export const exportTable = ({ columns, rows, filename, format }) =>
  exportWorkbook({ sheets: [{ name: filename, columns, rows }], filename, format });
